import './BatchEditLine.css';
import { useMemo } from 'react';
import { ApButtonSecondary } from '@alixpartners/ui-components';
import Select from 'components/UIComponents/Select';

import {
  ItemEditBoolean,
  ItemEditDate,
  ItemEditDateTime,
  ItemEditDecimal,
  ItemEditNumber,
  ItemEditText,
  ItemEditUnknown,
} from './ItemEdit';
import { ItemEditEnumarable } from './ItemEditEnumarable';
import { IBatchEditableColumns, IBatchItem } from '../contractsBatchEdit.type';
import { getSelectOptions } from '../utils/columnItems';
import { ContractReviewersEdit } from './ContractReviewersEdit';
import { useEditActionField } from 'hooks/useEditActionField';
import { SectionType } from 'pages/ClaimsContractsToolPage/types';
import { ContractCascadingBatchItem } from './ContractCascadingBatchItems/ContractCascadingBatchItem';

export type BatchEditLineProps<BI extends IBatchItem> = {
  item: BI;
  optionItems: IBatchEditableColumns[];
  onChange: (item: BI) => void;
  onRemove: (id: BI) => void;
  addCascadingItem: (item: IBatchItem) => void;
};

export const BatchEditLine = <BI extends IBatchItem>(
  props: BatchEditLineProps<BI>,
) => {
  const { resetActionFields } = useEditActionField(SectionType.Contracts);
  const { item, optionItems, onChange, onRemove, addCascadingItem } = props;

  const dataTypeOptions = useMemo(
    () => getSelectOptions(optionItems),
    [optionItems],
  );

  const handleTypeChange = (val: string) => {
    const col = optionItems.find((r) => r.propertyName === val);

    if (!col) return;

    if (col.dataType === 'group-multi-select') {
      onChange({
        ...item,
        propName: val,
        type: col.dataType,
        allowedValues: col.allowedValues,
        value: [],
      });
      return;
    }
    if (col.dataType === 'action-name-id') {
      // reset redux state here
      resetActionFields();
      onChange({
        ...item,
        propName: val,
        type: col.dataType,
        allowedValues: col.allowedValues,
        value: null,
      });
      return;
    }

    onChange({
      ...item,
      propName: val,
      type: col.dataType,
      allowedValues: col.allowedValues,
      value: '',
    });
  };

  const handleValueChange = (value: IBatchItem['value']) => {
    onChange({ ...item, value });
  };

  return (
    <div className="batchedit-line">
      <div className="batchedit-selections">
        <Select
          label="Select data type"
          options={dataTypeOptions}
          defaultValue={item.propName}
          onChange={(r) => handleTypeChange(r?.target?.value)}
        />
        <FieldValueEdit
          item={item}
          onChange={handleValueChange}
          addCascadingItem={addCascadingItem}
        />
      </div>
      <ApButtonSecondary className="ap-button-casual" onClick={() => onRemove(item)}>
        Remove
      </ApButtonSecondary>
    </div>
  );
};

export const FieldValueEdit = (props: {
  item: IBatchItem;
  onChange: (val: IBatchItem['value']) => void;
  addCascadingItem: (item: IBatchItem) => void;
}) => {
  const { item, onChange, addCascadingItem } = props;

  switch (item.type) {
    case 'int':
      return <ItemEditNumber value={item.value} onChange={onChange} />;
    case 'date':
      return <ItemEditDate value={item.value} onChange={onChange} />;
    case 'datetime':
      return <ItemEditDateTime value={item.value} onChange={onChange} />;
    case 'decimal':
      return <ItemEditDecimal value={item.value} onChange={onChange} />;
    case 'bool':
      return <ItemEditBoolean value={item.value} onChange={onChange} />;
    case 'string':
      return <ItemEditText value={item.value} onChange={onChange} />;

    case 'enum':
      return (
        <ItemEditEnumarable
          value={item.value}
          allowedItems={item.allowedValues}
          onChange={onChange}
        />
      );
    case 'group-multi-select': {
      return <ContractReviewersEdit label="Select value" onChange={onChange} />;
    }
    case 'contractCategoryType':
    case 'contractResolutionItems':
    case 'contractResolutionName': {
      return (
        <ContractCascadingBatchItem
          type={item.type}
          onChange={onChange}
          addCascadingItem={addCascadingItem}
          propName={item.propName}
        />
      );
    }
  }

  return <ItemEditUnknown />;
};
